import { IExtraKey } from "@shared/interfaces";
import {
  SensorDataFile,
  SensorDataSession,
  SensorDataSessionMarker,
  SensorDataSessionMarkerDot,
  Station,
} from "@shared/models";

export const RIGHT_MARKERS_WIDTH = 500;

export enum EXCEL_COLUMNS {
  SESSION_NAME = "Session Name",
  MARKER_TYPE = "Marker Type",
  FILE_NAME = "File Name",
  STATION_CODE = "SDH Code",
  TIMESTAMP = "Timestamp",
  SAMPLE_NUMBER = "Sample Number",
  FILE_ROW_NUMBER = "File Row Number",
  VALUE = "Value",
  MILLISEC_DIF = "Millisec Dif",
  RATE = "Rate",
}

export const getMillisecFilesDif = (
  dots: SensorDataSessionMarkerDot[],
  currentDot: SensorDataSessionMarkerDot,
  index: number,
) => {
  const prevItem = dots[Number(index || 0) - 1];
  if (!prevItem || prevItem.session_file_id !== currentDot.session_file_id) return "-";

  return (currentDot.seconds - prevItem.seconds).toFixed(5);
};

export const getMillisecStationsDif = (
  dots: SensorDataSessionMarkerDot[],
  currentDot: SensorDataSessionMarkerDot,
  index: number,
) => {
  const prevItem = dots[Number(index || 0) - 1];
  if (!prevItem || prevItem.session_station !== currentDot.session_station) return "-";
  return Math.abs((currentDot.seconds - prevItem.seconds) / 1000).toFixed(5);
};

export const prepareExcelData = (
  session: SensorDataSession,
  markers: SensorDataSessionMarker[],
  dots: SensorDataSessionMarkerDot[],
  files: SensorDataFile[],
  stations: Partial<Station>[],
) => {
  if (!stations.length) {
    return prepareExcelFilesData(session, markers, dots, files);
  }

  return prepareExcelStationsData(session, markers, dots, stations);
};

export const prepareExcelFilesData = (
  session: SensorDataSession,
  markers: SensorDataSessionMarker[],
  dots: SensorDataSessionMarkerDot[],
  files: SensorDataFile[],
) => {
  const filesKeyName = files.reduce((r: { [key: string]: SensorDataFile }, i) => {
    r[String(i.id)] = i;
    return r;
  }, {});

  return markers.reduce(
    (
      r: {
        name: string;
        items: IExtraKey<string | number>[];
      }[],
      i,
    ) => {
      const markerDots = dots.filter((d) => d.session_marker_id === i.id);

      if (!markerDots.length) {
        return [
          ...r,
          {
            name: i.title,
            items: [
              {
                [EXCEL_COLUMNS.SESSION_NAME]: "",
                [EXCEL_COLUMNS.MARKER_TYPE]: "",
                [EXCEL_COLUMNS.FILE_NAME]: "",
                [EXCEL_COLUMNS.TIMESTAMP]: "",
                [EXCEL_COLUMNS.SAMPLE_NUMBER]: "",
                [EXCEL_COLUMNS.FILE_ROW_NUMBER]: "",
                [EXCEL_COLUMNS.VALUE]: "",
                [EXCEL_COLUMNS.MILLISEC_DIF]: "",
                [EXCEL_COLUMNS.RATE]: "",
              },
            ],
          },
        ];
      }

      return [
        ...r,
        {
          name: i.title,
          items: markerDots.map((d, index) => {
            return {
              [EXCEL_COLUMNS.SESSION_NAME]: session.name,
              [EXCEL_COLUMNS.MARKER_TYPE]: i.title,
              [EXCEL_COLUMNS.FILE_NAME]: d.session_file_id ? filesKeyName[d.session_file_id].custom_name : "-",
              [EXCEL_COLUMNS.TIMESTAMP]: d.time,
              [EXCEL_COLUMNS.SAMPLE_NUMBER]: d.number,
              [EXCEL_COLUMNS.FILE_ROW_NUMBER]: d.row_number,
              [EXCEL_COLUMNS.VALUE]: d.value,
              [EXCEL_COLUMNS.MILLISEC_DIF]: getMillisecStationsDif(markerDots, d, index),
              [EXCEL_COLUMNS.RATE]: d.session_file_id ? filesKeyName[d.session_file_id].rate : "-",
            };
          }),
        },
      ];
    },
    [],
  );
};

export const prepareExcelStationsData = (
  session: SensorDataSession,
  markers: SensorDataSessionMarker[],
  dots: SensorDataSessionMarkerDot[],
  stations: Partial<Station>[],
) => {
  const stationsKeyName = stations.reduce((r: Record<string, Partial<Station>>, i) => {
    r[String(i.station_code)] = i;
    return r;
  }, {});

  return markers.reduce(
    (
      r: {
        name: string;
        items: IExtraKey<string | number>[];
      }[],
      i,
    ) => {
      const markerDots = dots.filter((d) => d.session_marker_id === i.id);

      if (!markerDots.length) {
        return [
          ...r,
          {
            name: i.title,
            items: [
              {
                [EXCEL_COLUMNS.SESSION_NAME]: "",
                [EXCEL_COLUMNS.MARKER_TYPE]: "",
                [EXCEL_COLUMNS.STATION_CODE]: "",
                [EXCEL_COLUMNS.TIMESTAMP]: "",
                [EXCEL_COLUMNS.SAMPLE_NUMBER]: "",
                [EXCEL_COLUMNS.VALUE]: "",
                [EXCEL_COLUMNS.MILLISEC_DIF]: "",
                [EXCEL_COLUMNS.RATE]: "",
              },
            ],
          },
        ];
      }

      return [
        ...r,
        {
          name: i.title,
          items: markerDots.map((d, index) => {
            return {
              [EXCEL_COLUMNS.SESSION_NAME]: session.name,
              [EXCEL_COLUMNS.MARKER_TYPE]: i.title,
              [EXCEL_COLUMNS.STATION_CODE]: d.session_station
                ? String(stationsKeyName[d.session_station]?.station_code)
                : "-",
              [EXCEL_COLUMNS.TIMESTAMP]: d.time,
              [EXCEL_COLUMNS.SAMPLE_NUMBER]: d.number,
              [EXCEL_COLUMNS.VALUE]: d.value,
              [EXCEL_COLUMNS.MILLISEC_DIF]: getMillisecStationsDif(markerDots, d, index),
            };
          }),
        },
      ];
    },
    [],
  );
};
